import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import {
	ButtonGroup,
	ClickAwayListener,
	MenuItem,
	MenuList,
	Popper,
} from '@mui/material';

import { useRef, useState, useEffect } from 'react';

import { useSelector } from 'react-redux';

import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { RootState } from 'common/store/AppStore';

import { getPromptTableData } from 'features/Settings/PromptBuilder/api';
import { IPromptBuilderParams } from 'features/Settings/PromptBuilder/api/type-def';

import ButtonComp, { IButtonProps } from '../Button/ButtonComp';

import styles from './PromptSelect.module.scss';

interface IPromptSelect extends IButtonProps {
	aiModelKey: string;
	onSelectPrompt: (option: any) => void;
	groupClass?: string;
	iconClass?: string;
}

const PromptSelect = (props: IPromptSelect) => {
	const {
		aiModelKey,
		onSelectPrompt,
		groupClass,
		iconClass,
		children,
		...buttonProps
	} = props;
	const [open, setOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const { OK } = HTTP_STATUS_CODES;

	const anchorRef = useRef<HTMLDivElement>(null);
	const [selectedIndex, setSelectedIndex] = useState('');
	const [options, setOptions] = useState<IPromptBuilderParams[]>([]);

	const promptSearchText = useSelector(
		(state: RootState) => state.common.promptSearchText,
	);

	const handleMenuItemClick = (
		event: React.MouseEvent<HTMLLIElement, MouseEvent>,
		selectedOption: any,
	) => {
		setOpen(false);
		setSelectedIndex(selectedOption);
		onSelectPrompt(selectedOption);
	};

	const handleIconClicks = () => {
		setOpen((prevOpen) => !prevOpen);
	};

	const handleClose = (event: Event) => {
		if (
			anchorRef.current &&
			anchorRef.current.contains(event.target as HTMLElement)
		) {
			return;
		}

		setOpen(false);
	};
	const getAIModelPrompts = async () => {
		setIsLoading(true);
		const params = {
			ai_model_key: aiModelKey,
			is_draft: false,
		};
		const response = await getPromptTableData(params, promptSearchText);
		if (response?.responseBody && response?.statusCode === OK) {
			const responseData = response?.responseBody;
			const filteredData = responseData?.data.filter(
				(e) => e.is_active !== true,
			);
			setOptions(filteredData);
		}
		setIsLoading(false);
	};

	useEffect(() => {
		if (aiModelKey) {
			getAIModelPrompts();
		}
	}, [aiModelKey]);
	const promptOptionVisible = options.length === 0 ? !!isLoading : true;

	return (
		<>
			{isLoading || buttonProps.loading ? (
				<ButtonComp
					loading
					compClass={`${styles['btn-component']} ${buttonProps.compClass}`}
					{...buttonProps}
				>
					{children}
				</ButtonComp>
			) : (
				<ButtonGroup
					variant="contained"
					ref={anchorRef}
					aria-label="Button group with a nested menu"
					className={groupClass}
				>
					<ButtonComp
						{...buttonProps}
						compClass={`${styles['btn-component']} ${buttonProps.compClass} ${
							promptOptionVisible && styles['btn-component-border']
						}`}
					>
						{children}
					</ButtonComp>
					{promptOptionVisible && (
						<ButtonComp
							loading={isLoading}
							btnType={buttonProps.btnType}
							size="small"
							disabled={buttonProps.disabled}
							compClass={`${styles['btn-down-icon']} ${iconClass}`}
							clickHandler={handleIconClicks}
							data-testid="btn-arraow-click"
						>
							<ExpandMoreRoundedIcon />
						</ButtonComp>
					)}
				</ButtonGroup>
			)}

			<Popper
				className={styles['menu-container']}
				open={open}
				anchorEl={anchorRef.current}
			>
				<ClickAwayListener onClickAway={handleClose}>
					<MenuList
						id="split-button-menu"
						autoFocusItem
						className={styles['menu-list-container']}
					>
						{options.map((option) => (
							<MenuItem
								key={option.key}
								data-testid={option.key}
								selected={option.key === selectedIndex}
								onClick={(event) => handleMenuItemClick(event, option)}
							>
								{option.key}
							</MenuItem>
						))}
					</MenuList>
				</ClickAwayListener>
			</Popper>
		</>
	);
};
export default PromptSelect;
